import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, Typography, Box, Chip, IconButton } from '@mui/material';
import { ArrowBack } from '@material-ui/icons';
import { useHistory, useParams } from 'react-router-dom';
import useStyles from '../../../assets/styles/HomeStyles';
import { pdf } from '@react-pdf/renderer';
import { useReactToPrint } from 'react-to-print';
import TeachingPlanPdfDoc from './components/TeachingPlanPdfDoc.comp';
import TeachingPlanHtmlDoc from './components/TeachingPlanHtmlDoc.comp';
import { getInsiderProgramService, listV3Level7DQCodeService, getLearningProgramPreviewService } from '../../../services/learning_programs.service';
import LoaderWithBackDrop from '../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp';
import { toastWarning } from '../../../utils/utils';
import { pickUiLocaleFromProgram } from "../../../utils/dq_lc";

const DQProProgResourcesPage = () => {
    const classes = useStyles();
    const history = useHistory();
    const { code } = useParams();

    const [programData, setProgramData] = useState(null);
    const [modules, setModules] = useState([]);
    const [programDuration, setProgramDuration] = useState(0);
    const [programAudience, setProgramAudience] = useState('');
    const [dqcoList, setDqcoList] = useState([]);
    const [loading, setLoading] = useState(true);  // Start with true to prevent premature clicks
    const [pdfLoading, setPdfLoading] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState();

    const isEducatorView = true; // Default to educator view for Teaching Plan

    // Ref for HTML-to-PDF printing (supports all languages!)
    const teachingPlanRef = useRef();

    // React-to-print handler (BEST for multi-language support)
    const handlePrintTeachingPlan = useReactToPrint({
        content: () => teachingPlanRef.current,
        documentTitle: `${programData?.full_name || 'Program'} - Teaching Plan`,
        onBeforeGetContent: () => {
            console.log('Preparing to print Teaching Plan...');
        },
        onAfterPrint: () => {
            console.log('Teaching Plan printed successfully');
        },
        onPrintError: (error) => {
            console.error('Print error:', error);
            toastWarning('Failed to generate PDF');
        },
    });

    useEffect(() => {
        const fetchAllData = async () => {
            setLoading(true);
            
            // Fetch program data first to determine language
            const determinedLanguage = await fetchProgramData();
            
            // Then fetch DQ codes with the determined language
            await fetchDQCodes(determinedLanguage);
            
            setLoading(false);
        };

        fetchAllData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [code]); // Only refetch when code changes, not when language changes

    const fetchProgramData = async () => {
        try {
            console.log('Fetching program data for code:', code);

            // Fetch basic program info FIRST to determine language
            const response = await getInsiderProgramService(code);
            console.log('API Response:', response);

            let languageToUse = selectedLanguage;

            if (response?.data?.learning_program) {
                const program = response.data.learning_program;
                console.log('Program data:', program);
                setProgramData(program);

                // Default the Teaching Plan language to the program's non-English locale if available.
                const preferredLocale = pickUiLocaleFromProgram(program);
                if (preferredLocale && preferredLocale !== selectedLanguage) {
                    languageToUse = preferredLocale;
                    setSelectedLanguage(preferredLocale);
                }
            } else {
                console.warn('No learning_program found in response');
            }

            // Fetch program preview with modules using the determined language
            const previewRes = await getLearningProgramPreviewService(code);
            console.log('Preview Response:', previewRes);
            console.log('Using language for modules:', languageToUse);

            if (!previewRes?.data?.is_outline_ready) {
                console.warn('Learning program outline not ready yet!');
                toastWarning('Learning program outline not ready yet!');
                return;
            }

            const previewsByLang = previewRes?.data?.program_layout_previews;
            const programPreview =
                previewsByLang?.[languageToUse]?.program_layout_preview ||
                previewsByLang?.['en-US']?.program_layout_preview ||
                previewRes?.data?.program_layout_preview;

            if (programPreview) {
                console.log('Program preview:', programPreview);

                if (programPreview.modules && Array.isArray(programPreview.modules)) {
                    console.log('Modules found:', programPreview.modules.length, programPreview.modules);

                    // Map the modules to match the expected format
                    const mappedModules = programPreview.modules.map(mod => ({
                        module_code: mod.module_code || '',
                        module_name: mod.module_title || '',
                        module_educator_description: mod.description_for_educator || '',
                        module_learner_description: mod.description_for_learner || '',
                        module_duration: mod.total_duration_minutes || 0,
                        unique_dqco: mod.unique_dqco || [],
                        unique_frame_arc: mod.unique_frame_arc || [],
                        units: (mod.units || []).map(unit => ({
                            unit_title: unit.unit_title || '',
                            unit_educator_description: unit.description_for_educator || '',
                            unit_learner_description: unit.description_for_learner || '',
                            unit_duration: unit.total_duration_minutes || 0,
                            lessons: (unit.sessions || []).map(session => ({  // API returns 'sessions' not 'lessons'
                                lesson_title: session.session_title || '',
                                lesson_educator_description: session.description_for_educator || '',
                                lesson_learner_description: session.description_for_learner || '',
                                lesson_duration: session.duration_minutes || 0,  // Also different field name
                                session_unique_ksab: session.unique_ksab || [],
                                session_learning_objectives: session.session_learning_objectives || []
                            }))
                        }))
                    }));

                    setModules(mappedModules);
                    console.log('Mapped modules:', mappedModules);
                } else {
                    console.warn('No modules array found in program preview');
                }

                // Set duration and audience from preview
                if (programPreview.total_duration_minutes) {
                    setProgramDuration(programPreview.total_duration_minutes);
                    console.log('Program duration:', programPreview.total_duration_minutes);
                }

                if (programPreview.target_age_group) {
                    setProgramAudience(programPreview.target_age_group);
                    console.log('Program audience:', programPreview.target_age_group);
                }
            }
            
            return languageToUse; // Return the determined language
        } catch (error) {
            console.error('Failed to fetch program data:', error);
            toastWarning('Failed to load program data');
            return selectedLanguage || 'en-US'; // Return fallback
        } finally {
            console.log('Program data fetch complete');
        }
    };

    const fetchDQCodes = async (language) => {
        try {
            const resp = await listV3Level7DQCodeService(language || selectedLanguage);
            if (resp?.data?.dqco) {
                setDqcoList(resp.data.dqco);
            }
        } catch (error) {
            console.error('Failed to fetch DQ codes:', error);
        }
    };

    const handleGenerateTeachingPlanPdfDoc = async () => {
        // console.log('=== handleGenerateTeachingPlan called ===');
        // console.log('Current state:', {
        //     pdfLoading,
        //     modulesCount: modules?.length,
        //     modules,
        //     programData,
        //     programAudience,
        //     programDuration,
        //     dqcoListCount: dqcoList?.length
        // });

        if (pdfLoading) {
            console.log('Already generating PDF, returning...');
            return;
        }

        if (!modules || modules.length === 0) {
            console.error('No modules available. modules:', modules);
            toastWarning('No program content available to generate Teaching Plan');
            return;
        }

        try {
            setPdfLoading(true);
            console.log('Generating PDF with data...');
            const doc = (
                <TeachingPlanPdfDoc
                    programData={programData}
                    programAudience={programAudience}
                    programDuration={programDuration}
                    modules={modules}
                    isEducatorView={isEducatorView}
                    dqcoList={dqcoList}
                    selectedLanguage={selectedLanguage}
                />
            );
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            console.log('PDF opened successfully');
            // Revoke after some time to allow loading
            setTimeout(() => URL.revokeObjectURL(url), 120000);
        } catch (error) {
            console.error('Failed to generate Teaching Plan PDF', error);
            toastWarning('Failed to generate Teaching Plan PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleBack = () => {
        history.push(`/dq_pro/products/program/dashboard/${code}`);
    };

    const handleCardClick = (cardTitle) => {
        // Prevent clicks while data is loading
        if (loading) {
            console.log('Still loading data, please wait...');
            return;
        }

        if (cardTitle === 'Teaching Plan') {
            // Use HTML-based PDF generation (supports ALL languages automatically!)
            handlePrintTeachingPlan();
        }
    };

    const cardsData = [
        { title: 'Teaching Plan', comingSoon: false },
        { title: 'API Keys and Documentation', comingSoon: true },
        { title: 'PowerPoint Slides', comingSoon: true }
    ];

    return (
        <>
            {(loading || pdfLoading) && <LoaderWithBackDrop loading={loading} />}
            <Box
                className={classes.pageContainer}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '80vh',
                    position: 'relative',
                    paddingTop: 2
                }}
            >
                <IconButton
                    onClick={handleBack}
                    sx={{
                        position: 'absolute',
                        top: { xs: 16, md: 24 },
                        left: { xs: 16, md: 24 },
                        zIndex: 10,
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: '#ffffff',
                        color: '#0097b2',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                        border: '1px solid rgba(0,151,178,0.15)',
                        transition: 'all 0.25s ease',
                    }}
                >
                    <ArrowBack />
                </IconButton>
                <Typography
                    variant="h3"
                    className={classes.title}
                    sx={{ textAlign: 'center', mb: 4, mt: { xs: 4, md: 0 } }}
                >
                    Your Program Resources
                </Typography>

                <Box
                    className={classes.cardsContainer}
                    sx={{
                        flexDirection: { xs: 'column', sm: 'row' },
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: { xs: '100%', sm: '85%' },
                        flex: 1,
                        display: 'flex',
                        pointerEvents: loading ? 'none' : 'auto',  // Disable clicks while loading
                        opacity: loading ? 0.6 : 1,  // Visual feedback that interaction is disabled
                        transition: 'opacity 0.3s ease'
                    }}
                >
                    {cardsData.map((card, index) => (
                        <Card
                            key={index}
                            className={classes.card}
                            onClick={() => !card.comingSoon && handleCardClick(card.title)}
                            sx={{
                                backgroundColor: card.comingSoon ? '#D9D9D9' : '',
                                border: card.comingSoon ? 'none' : '1px solid #ff6468ff',
                                position: 'relative',
                                width: { xs: '90%', sm: '23%' },
                                minHeight: '200px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                paddingBottom: '10px',
                                cursor: card.comingSoon ? 'default' : 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': card.comingSoon ? {} : {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 24px rgba(255,100,104,0.25)',
                                }
                            }}
                        >
                            {card.comingSoon && (
                                <Chip
                                    label="Coming Soon"
                                    sx={{
                                        position: 'absolute',
                                        top: '14px',
                                        left: '50%',
                                        transform: 'translateX(-50%)',
                                        height: '40px',
                                        width: '75%',
                                        borderRadius: '0px',
                                        backgroundColor: '#000000',
                                        color: 'white',
                                        mb: 3,
                                        fontWeight: 'bold',
                                        fontSize: '21px',
                                    }}
                                />
                            )}
                            <CardContent className={classes.cardContent}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontSize: { sm: '1.3rem', md: '1.5rem' },
                                        fontWeight: 'bold',
                                        lineHeight: '1.1',
                                        marginTop: card.comingSoon ? '35px' : '20px',
                                        color: card.comingSoon ? '#ffffff' : '#000000',
                                        textAlign: 'center'
                                    }}
                                >
                                    {card.title}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            </Box>

            {/* Hidden HTML component for printing - Supports ALL languages! */}
            <div style={{ display: 'none' }}>
                <TeachingPlanHtmlDoc
                    ref={teachingPlanRef}
                    programData={{
                        ...programData,
                        full_name: selectedLanguage !== 'en-US' 
                            ? (programData?.translations?.[selectedLanguage]?.full_name || programData?.translations?.[selectedLanguage]?.name || programData?.full_name)
                            : programData?.full_name,
                        description: selectedLanguage !== 'en-US'
                            ? (programData?.translations?.[selectedLanguage]?.description || programData?.description)
                            : programData?.description
                    }}
                    programAudience={programAudience}
                    programDuration={programDuration}
                    modules={modules}
                    isEducatorView={isEducatorView}
                    selectedLanguage={selectedLanguage}
                    uiLocale={selectedLanguage}
                />
            </div>
        </>
    );
};

export default DQProProgResourcesPage;
